import { Link, useParams } from "react-router-dom";
import { heartbeatState, templateStanding } from "@starsach/shared-types";
import { useCustomer } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";
import { viewAsUrl } from "../viewAs";
import { useRememberEnvironment } from "../components/Environment";
import { UsageCard } from "../components/UsageCard";
import { HeartbeatPill, TemplateStandingPill } from "../components/FleetPills";
import { Card, ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, vtag, btnSecondary } from "../components/ui";
import { modulePath, visibleModules } from "../lib/modules";
import { OnboardingPanel } from "./customer/OnboardingPanel";

/**
 * design.md "Account drill-down", now the environment's overview: what
 * the system knows about the account itself, and the way into each module
 * for it (section 8.9 - the tabs became modules).
 */
export function CustomerDetailPage() {
  const { environmentId = "" } = useParams();
  const { me } = useAuth();
  useRememberEnvironment(environmentId);
  const q = useCustomer(environmentId);
  if (q.isPending) return <LoadingState label="Loading customer" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { customer: c, currentTemplateVersion, enabledComponents } = q.data;
  const standing = templateStanding(c.template_version, currentTemplateVersion);
  const modules = visibleModules(me).filter((m) => m.environmentScoped);

  return (
    <>
      <PageHeader
        title={c.friendly_name}
        subtitle={`AWS Account ${c.aws_account_number}`}
        actions={
          <div className="flex flex-wrap items-start gap-6">
          {me?.role === "internal-team" && !me.viewAs && (
            // ADR-0011: the customer's own portal, in a new tab, read-only until asked otherwise. rel="opener" on purpose:
            // the session lives in sessionStorage, and only a tab with an opener inherits a copy (same origin, so nothing leaks).
            <a href={viewAsUrl(c.organisation_id, `/customers/${environmentId}`)} target="_blank" rel="opener" className={btnSecondary}>
              View their portal
            </a>
          )}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
            <dt className="text-neutral-500">Status</dt>
            <dd><StatusPill status={c.status} tone={c.status === "active" ? "ok" : "neutral"} /></dd>
            <dt className="text-neutral-500" title="When a test event last came through the same path a real alarm takes. The stack sends one on every create or update; the heartbeat is a separate, hourly signal.">
              Alarm path tested
            </dt>
            <dd>{c.connectivity_verified_at ? <RelativeTime iso={c.connectivity_verified_at} /> : <StatusPill status="never" tone="warning" />}</dd>
            <dt className="text-neutral-500">Heartbeat</dt>
            <dd><HeartbeatPill state={heartbeatState(c.last_heartbeat_at, c.connectivity_verified_at)} /> <RelativeTime iso={c.last_heartbeat_at} /></dd>
            <dt className="text-neutral-500">Template</dt>
            <dd>
              {c.template_version ? (
                <Link to={`/release-notes#template-${vtag(c.template_version)}`} className="text-accent-strong underline dark:text-accent">
                  {vtag(c.template_version)}
                </Link>
              ) : (
                vtag(c.template_version)
              )}
              {standing === "behind" && (
                <span className="ml-1 text-amber-700 dark:text-amber-300">
                  (current <Link to={`/release-notes#template-${vtag(currentTemplateVersion)}`} className="underline">{vtag(currentTemplateVersion)}</Link>)
                </span>
              )}
              {standing === "ahead" && <span className="ml-1"><TemplateStandingPill standing={standing} /></span>}
            </dd>
          </dl>
          </div>
        }
      />
      {me?.role === "customer" && (c.onboarding_status !== "active" || !c.last_heartbeat_at || standing === "behind") && <OnboardingPanel detail={q.data} />}
      {(c.forwarder_dlq_depth ?? 0) > 0 && (
        <p role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          {c.forwarder_dlq_depth} alarm notification{c.forwarder_dlq_depth === 1 ? "" : "s"} could not be delivered from this account and sit in its dead-letter queue.
        </p>
      )}
      <UsageCard environmentId={environmentId} />
      <Card title="In this environment" className="mt-4">
        <nav aria-label="Customer sections">
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {modules.map((m) => (
              <li key={m.id}>
                <Link to={modulePath(m, environmentId, m.sections?.[0]?.id)} className="block rounded-lg border border-neutral-200 px-4 py-3 text-sm font-medium text-ink hover:border-accent hover:bg-accent-soft dark:border-neutral-800 dark:text-white dark:hover:bg-neutral-800">
                  {m.label}
                  <span className="mt-0.5 block text-xs font-normal text-neutral-500">{describeModule(m.id, enabledComponents)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </Card>
    </>
  );
}

function describeModule(id: string, enabled: string[]): string {
  switch (id) {
    case "alarms": return "Open and resolved CloudWatch alarms";
    case "journeys": return enabled.includes("JourneyInspectorRole") ? "Paths, contacts, concurrency" : "Paths and concurrency";
    case "flow-configuration": return enabled.includes("FlowConfiguration") ? "Profiles, numbers, promotions" : "Not deployed in this account";
    case "quotas": return enabled.includes("QuotaIncreaseRequestRole") ? "Limits, usage, increase requests" : "Limits and usage";
    case "trusted-advisor": return "Findings from AWS";
    case "support": return "Open AWS Support cases";
    case "access": return "Who holds what here";
    case "audit": return "Every change the portal made";
    default: return "";
  }
}
